import React, { useState } from 'react';
import { supabase } from '../../../utils/supabase';
import LoadingSpinner from '../../../components/LoadingSpinner';
import Button from '../../../components/ui/Button';

const LoginSection = ({ onLoginSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [error, setError] = useState('');

  const handleAuth = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      let result;
      
      if (isSignUp) {
        result = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/`
          }
        });
      } else {
        result = await supabase.auth.signInWithPassword({
          email,
          password
        });
      }

      if (result.error) {
        setError(result.error.message);
      } else if (result.data?.user) {
        // Check if it's a company domain for bonus credits
        const domain = email.split('@')[1];
        const isCompanyDomain = !['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com'].includes(domain);
        
        if (isSignUp && isCompanyDomain) {
          // Add bonus credits for company domain signup
          // This would be handled in your backend
          console.log('Company domain detected, bonus credits will be added');
        }
        
        onLoginSuccess && onLoginSuccess(result.data.user);
      }
    } catch (error) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    setError('');

    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/`
        }
      });

      if (error) {
        setError(error.message);
      }
    } catch (error) {
      setError('Google sign-in failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-primary-light to-white rounded-2xl p-8 md:p-12">
      <div className="max-w-md mx-auto space-y-8">
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto">
            <span className="text-2xl text-white">📧</span>
          </div>
          <h2 className="text-2xl font-bold text-text-primary">
            {isSignUp ? 'Create Your Account' : 'Welcome Back'}
          </h2>
          <p className="text-text-secondary">
            {isSignUp 
              ? 'Get started with email validation today'
              : 'Sign in to continue validating emails'
            }
          </p>
        </div>

        {/* Free Credits Banner */}
        <div className="bg-primary text-white rounded-lg p-4 text-center">
          <div className="text-2xl font-bold mb-2">🎁 100 Free Credits</div>
          <p className="text-sm opacity-90">
            {isSignUp 
              ? 'Sign up with your company domain and get 100 free credits every month!'
              : 'New users get 100 free credits with company domain signup'
            }
          </p>
          <div className="text-xs opacity-75 mt-2">
            No credit card required • Cancel anytime
          </div>
        </div>

        {/* Auth Form */}
        <form onSubmit={handleAuth} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-text-primary mb-2">
              Email Address
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="your@company.com"
              required
            />
            {email && !email.includes('@') && (
              <p className="text-xs text-primary mt-1">
                💡 Use your company domain for bonus credits!
              </p>
            )}
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-text-primary mb-2">
              Password
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="••••••••"
              required
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <Button
            type="submit"
            variant="primary"
            size="md"
            fullWidth
            disabled={isLoading}
            loading={isLoading}
            animation="pulse"
          >
            {isLoading ? (
              <LoadingSpinner variant="button" text={isSignUp ? 'Creating Account...' : 'Signing In...'} size="small" />
            ) : (
              isSignUp ? 'Create Account' : 'Sign In'
            )}
          </Button>
        </form>

        {/* Divider */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-text-secondary">Or continue with</span>
          </div>
        </div>

        {/* Google Sign In */}
        <Button
          variant="outline"
          size="md"
          fullWidth
          onClick={handleGoogleSignIn}
          disabled={isLoading}
          animation="float"
          className="flex items-center justify-center gap-3"
        >
          {isLoading ? (
            <LoadingSpinner variant="button" text="Connecting..." size="small" />
          ) : (
            <>
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </>
          )}
        </Button>

        {/* Toggle Sign In/Sign Up */}
        <div className="text-center">
          <button
            onClick={() => setIsSignUp(!isSignUp)}
            className="text-primary hover:underline text-sm"
          >
            {isSignUp 
              ? 'Already have an account? Sign in'
              : "Don't have an account? Sign up"
            }
          </button>
        </div>

        {/* Benefits */}
        <div className="space-y-3">
          <h4 className="font-semibold text-text-primary">What you get:</h4>
          <div className="space-y-2 text-sm text-text-secondary">
            <div className="flex items-center gap-2">
              <span className="text-primary">✓</span>
              100 free email validation credits
            </div>
            <div className="flex items-center gap-2">
              <span className="text-primary">✓</span>
              Real-time validation with 99.9% accuracy
            </div>
            <div className="flex items-center gap-2">
              <span className="text-primary">✓</span>
              Bulk email processing
            </div>
            <div className="flex items-center gap-2">
              <span className="text-primary">✓</span>
              Detailed validation reports
            </div>
            <div className="flex items-center gap-2">
              <span className="text-primary">✓</span>
              API access for integration
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginSection; 